import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Paper,
    IconButton,
    Divider,
    Avatar
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BasicInfoStep, PackageDetailsStep, PricingStep } from './steps';
import PackageSummaryCard from '../../../../components/onboardingForms/PackageSummaryCard';
import { getBackendUserUrl } from 'utils/utils';

const STEPS = ['Basic Information', 'Package Details', 'Pricing & Availability'];

const PackagesOnboardingForm = () => {
    const history = useHistory();
    const [packages, setPackages] = useState([{
        id: 1,
        activeStep: 0,
        isComplete: false,
        data: {
            packageName: '',
            packageDescription: '',
            packageImage: null,
            category: '',
            subcategory: '',
            pricingStrategy: '',
            pricingData: {}
        },
        errors: {}
    }]);

    // Validate the basic info step for a package
    const validateBasicInfo = (packageData) => {
        const errors = {};
        
        if (!packageData.packageName?.trim()) {
            errors.packageName = 'Package name is required';
        }
        
        if (!packageData.packageDescription?.trim()) {
            errors.packageDescription = 'Package description is required';
        }
        
        if (!packageData.packageImage) {
            errors.packageImage = 'Package image is required';
        }
        
        return errors;
    };

    // Validate the package details step
    const validatePackageDetails = (packageData) => {
        const errors = {};
        
        if (!packageData.category?.trim()) {
            errors.category = 'Please select a category';
        }
        
        // Only validate subcategory if category is not Sponsor
        if (packageData.category !== 'Sponsor' && !packageData.subcategory?.trim()) {
            errors.subcategory = 'Please select a subcategory';
        }
        
        return errors;
    };

    // Validate the pricing step
    const validatePricing = (packageData) => {
        const errors = {};
        
        if (!packageData.pricingStrategy) {
            errors.pricingStrategy = 'Please select a pricing strategy';
            return errors;
        }

        const strategy = packageData.pricingStrategy;
        const data = packageData.pricingData || {};

        // Validate sponsor pricing
        if (strategy === 'sponsor') {
            if (!data.tiers || data.tiers.length === 0) {
                errors.pricingData = { tiers: 'Please add at least one sponsor tier' };
                return errors;
            }

            const tierErrors = data.tiers.map(tier => {
                const tierError = {};
                if (!tier.name?.trim()) tierError.name = 'Tier name is required';
                if (!tier.contributionType) tierError.contributionType = 'Please select a contribution type';
                if (tier.contributionType === 'Monetary') {
                    if (!tier.priceRange) tierError.priceRange = 'Please select a price range';
                    if (tier.priceRange === 'custom' && !tier.customPriceRange) {
                        tierError.customPriceRange = 'Please enter a custom price range';
                    }
                } else if (tier.contributionType === 'In-Kind') {
                    if (!tier.inKindDetails?.trim()) tierError.inKindDetails = 'Please provide in-kind contribution details';
                }
                return Object.keys(tierError).length > 0 ? tierError : null;
            });

            if (tierErrors.some(error => error !== null)) {
                errors.pricingData = { tiers: tierErrors };
            }
            return errors;
        }

        // Validate simple per-unit pricing
        if (!['tiered_per_person', 'combo', 'per_menu_item', 'per_event'].includes(strategy)) {
            if (!data.price || data.price <= 0) {
                errors.pricingData = { price: 'Please enter a valid price' };
            }
            return errors;
        }

        // Validate tiered pricing
        if (strategy === 'tiered_per_person') {
            if (!data.tiers || data.tiers.length === 0) {
                errors.pricingData = { tiers: 'Please add at least one tier' };
                return errors;
            }

            const tierErrors = data.tiers.map(tier => {
                const tierError = {};
                if (!tier.name?.trim()) tierError.name = 'Tier name is required';
                if (!tier.minGuests || Number(tier.minGuests) < 0) tierError.minGuests = 'Invalid minimum guests';
                if (!tier.maxGuests || Number(tier.maxGuests) <= Number(tier.minGuests)) tierError.maxGuests = 'Max guests must be greater than min guests';
                if (!tier.pricePerGuest || Number(tier.pricePerGuest) <= 0) tierError.pricePerGuest = 'Invalid price';
                return Object.keys(tierError).length > 0 ? tierError : null;
            });

            if (tierErrors.some(error => error !== null)) {
                errors.pricingData = { tiers: tierErrors };
            }
        }

        // Validate menu items
        if (strategy === 'per_menu_item') {
            if (!data.items || data.items.length === 0) {
                errors.pricingData = { items: 'Please add at least one menu item' };
                return errors;
            }

            const itemErrors = data.items.map(item => {
                const itemError = {};
                if (!item.name?.trim()) itemError.name = 'Item name is required';
                if (!item.price || item.price <= 0) itemError.price = 'Invalid price';
                return Object.keys(itemError).length > 0 ? itemError : null;
            });

            if (itemErrors.some(error => error !== null)) {
                errors.pricingData = { items: itemErrors };
            }
        }

        // Validate combo packages
        if (strategy === 'combo') {
            if (!data.packages || data.packages.length === 0) {
                errors.pricingData = { packages: 'Please add at least one package' };
                return errors;
            }

            const packageErrors = data.packages.map(pkg => {
                const pkgError = {};
                if (!pkg.name?.trim()) pkgError.name = 'Package name is required';
                if (!pkg.description?.trim()) pkgError.description = 'Package description is required';
                if (!pkg.price || pkg.price <= 0) pkgError.price = 'Invalid price';
                return Object.keys(pkgError).length > 0 ? pkgError : null;
            });

            if (packageErrors.some(error => error !== null)) {
                errors.pricingData = { packages: packageErrors };
            }
        }

        // Validate flat fee
        if (strategy === 'per_event') {
            if (!data.flatFee || data.flatFee <= 0) {
                errors.pricingData = { flatFee: 'Please enter a valid flat fee' };
            }
            if (!data.notes?.trim()) {
                errors.pricingData = { ...errors.pricingData, notes: 'Please provide duration/guest cap notes' };
            }
        }

        return errors;
    };

    // Handle field changes for a specific package
    const handleFieldChange = (packageId, field, value) => {
        setPackages(prevPackages => 
            prevPackages.map(pkg => 
                pkg.id === packageId
                    ? {
                        ...pkg,
                        data: {
                            ...pkg.data,
                            [field]: value
                        }
                    }
                    : pkg
            )
        );
    };

    // Add a new package
    const handleAddPackage = () => {
        const newPackageId = Math.max(...packages.map(p => p.id)) + 1;
        setPackages([
            ...packages,
            {
                id: newPackageId,
                activeStep: 0,
                isComplete: false,
                data: {
                    packageName: '',
                    packageDescription: '',
                    packageImage: null,
                    category: '',
                    subcategory: '',
                    pricingStrategy: '',
                    pricingData: {}
                },
                errors: {}
            }
        ]);
    };

    // Remove a package
    const handleRemovePackage = (packageId) => {
        if (packages.length === 1) {
            toast.error('You must have at least one package');
            return;
        }
        setPackages(packages.filter(pkg => pkg.id !== packageId));
    };

    // Handle edit for a specific package
    const handleEdit = (packageId) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg =>
                pkg.id === packageId
                    ? {
                        ...pkg,
                        isComplete: false,
                        activeStep: 0,
                        errors: {},
                        data: {
                            ...pkg.data,
                            // Reset pricing data when editing
                            pricingData: {},
                            // Reset pricing strategy when editing
                            pricingStrategy: '',
                            // Keep other data intact
                            packageName: pkg.data.packageName,
                            packageDescription: pkg.data.packageDescription,
                            packageImage: pkg.data.packageImage,
                            category: pkg.data.category,
                            subcategory: pkg.data.subcategory
                        }
                    }
                    : pkg
            )
        );
    };

    // Handle back step for a specific package
    const handleBack = (packageId) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg =>
                pkg.id === packageId
                    ? {
                        ...pkg,
                        activeStep: pkg.activeStep - 1,
                        errors: {},
                        // Reset pricing data when going back from pricing step
                        data: pkg.activeStep === 2 ? {
                            ...pkg.data,
                            pricingData: {},
                            pricingStrategy: ''
                        } : pkg.data
                    }
                    : pkg
            )
        );
    };

    // Handle next step for a specific package
    const handleNext = (packageId) => {
        setPackages(prevPackages =>
            prevPackages.map(pkg => {
                if (pkg.id !== packageId) return pkg;

                // Validate current step
                let errors = {};
                if (pkg.activeStep === 0) {
                    errors = validateBasicInfo(pkg.data);
                } else if (pkg.activeStep === 1) {
                    errors = validatePackageDetails(pkg.data);
                } else if (pkg.activeStep === 2) {
                    errors = validatePricing(pkg.data);
                }

                if (Object.keys(errors).length > 0) {
                    toast.error('Please fill in all required fields correctly');
                    return {
                        ...pkg,
                        errors
                    };
                }

                // If it's the last step, mark as complete
                if (pkg.activeStep === STEPS.length - 1) {
                    return {
                        ...pkg,
                        isComplete: true,
                        errors: {}
                    };
                }

                // Move to next step
                return {
                    ...pkg,
                    activeStep: pkg.activeStep + 1,
                    errors: {}
                };
            })
        );
    };

    // Render step content for a specific package
    const renderStepContent = (pkg) => {
        switch (pkg.activeStep) {
            case 0:
                return (
                    <BasicInfoStep
                        packageData={pkg.data}
                        onFieldChange={(field, value) => handleFieldChange(pkg.id, field, value)}
                        errors={pkg.errors}
                    />
                );
            case 1:
                return (
                    <PackageDetailsStep
                        packageData={pkg.data}
                        onFieldChange={(field, value) => handleFieldChange(pkg.id, field, value)}
                        errors={pkg.errors}
                        onNext={() => handleNext(pkg.id)}
                    />
                );
            case 2:
                return (
                    <PricingStep
                        packageData={pkg.data}
                        onFieldChange={(field, value) => handleFieldChange(pkg.id, field, value)}
                        errors={pkg.errors}
                    />
                );
            default:
                return null;
        }
    };

    // Render package content for a specific package
    const renderPackageContent = (pkg) => {
        if (pkg.isComplete) {
            return (
                <PackageSummaryCard
                    packageData={pkg.data}
                    onEdit={() => handleEdit(pkg.id)}
                />
            );
        }

        return (
            <>
                <Stepper activeStep={pkg.activeStep} sx={{ mb: 4 }}>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {renderStepContent(pkg)}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {pkg.activeStep > 0 && (
                        <Button
                            variant="outlined"
                            onClick={() => handleBack(pkg.id)}
                            sx={{ mr: 2 }}
                        >
                            Back
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={() => handleNext(pkg.id)}
                    >
                        {pkg.activeStep === STEPS.length - 1 ? 'Finish' : 'Next'}
                    </Button>
                </Box>
            </>
        );
    };

    // Handle form submission
    const handleSubmit = async () => {
        // Ensure all packages are complete
        if (!packages.every(pkg => pkg.isComplete)) {
            toast.error('Please complete all packages before submitting');
            return;
        }

        try {
            // Create FormData for file uploads
            const formData = new FormData();

            // Get company ID from session storage
            const companyId = sessionStorage.getItem('companyId');
            
            // Prepare the data for submission
            const packagesDataForJSON = packages.map(pkg => {
                // Only include the fields that are actually collected
                const { id, isComplete } = pkg;
                const {
                    packageName,
                    packageDescription,
                    category,
                    subcategory,
                    pricingStrategy,
                    pricingData
                } = pkg.data;
                return {
                    id,
                    packageName,
                    packageDescription,
                    category,
                    subcategory,
                    pricingStrategy,
                    pricingData,
                    isComplete,
                    companyId // Include company ID for each package
                };
            });
            
            // Add the JSON data (without file objects)
            formData.append('packagesData', JSON.stringify(packagesDataForJSON));
            
            // Add each package's image separately
            packages.forEach(pkg => {
                if (pkg.data.packageImage instanceof File) {
                    const file = pkg.data.packageImage;
                    console.log(`File object details for package ${pkg.id}:`, {
                        name: file.name,
                        type: file.type,
                        size: file.size,
                        lastModified: new Date(file.lastModified).toISOString()
                    });
                    
                    if (file.size === 0) {
                        console.warn(`Warning: File for package ${pkg.id} has zero size!`);
                    }
                    
                    try {
                        // Use the correct key format that backend expects
                        const fileKey = `packageImage_${pkg.id}`;
                        console.log(`Adding image with key "${fileKey}" for package ${pkg.id}`);
                        formData.append(fileKey, file, file.name);
                    } catch (err) {
                        console.error(`Error appending file for package ${pkg.id}:`, err);
                    }
                } else if (pkg.data.packageImage) {
                    console.log(`Package ${pkg.id} has non-File packageImage:`, typeof pkg.data.packageImage);
                } else {
                    console.log(`Package ${pkg.id} has no packageImage`);
                }
            });

            // Show loading toast
            const loadingToast = toast.loading('Submitting packages...');
            
            const apiUrl = getBackendUserUrl() + '/vendor/add_packages';
            // Make the API call to the backend
            const response = await fetch(apiUrl, {
                method: 'POST',
                body: formData,
                credentials: 'include', // Include cookies and auth headers
                // Don't set Content-Type - browser will set it with proper boundary for multipart/form-data
            });
            
            // Dismiss the loading toast
            toast.dismiss(loadingToast);
            
            // Check if the request was successful
            if (!response.ok) {
                // Extract error message if available
                let errorMessage = 'Failed to submit packages';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch (parseError) {
                    console.error('Error parsing error response:', parseError);
                }
                
                // Show error toast
                toast.error(errorMessage);
                return;
            }
            
            // Parse the successful response
            const result = await response.json();
            console.log('Packages submitted successfully:', result);
            
            // Show success toast
            toast.success(`${result.packages.length} packages submitted successfully!`);
            
            // Clear company types from session storage since we're done with onboarding
            sessionStorage.removeItem('selectedCompanyTypes');
            
            // Redirect to myevents page
            history.push('/myevents');
            
        } catch (error) {
            console.error('Error submitting packages:', error);
            toast.error('Failed to submit packages. Please try again.');
        }
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: '70px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4">Package Creation</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPackage}
                >
                    Add Another Package
                </Button>
            </Box>

            {packages.map((pkg, index) => (
                <Paper key={pkg.id} elevation={3} sx={{ mb: 4, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Package {index + 1}
                        </Typography>
                        <IconButton
                            onClick={() => handleRemovePackage(pkg.id)}
                            color="error"
                            disabled={packages.length === 1}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>

                    {renderPackageContent(pkg)}
                </Paper>
            ))}

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={handleSubmit}
                    disabled={!packages.every(pkg => pkg.isComplete)}
                    sx={{ minWidth: 200 }}
                >
                    Submit All Packages
                </Button>
            </Box>
        </Box>
    );
};

export default PackagesOnboardingForm;
